import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@environment/environment';
import { LoggerService } from '@app/core/infrastructure';
import { UserProfileUpdateInput, UserResponseSchema, AuthProvidersResponse } from './user-api.models';

/**
 * Pure API service for user-related HTTP operations
 * No state management - just HTTP calls
 */
@Injectable({
  providedIn: 'root'
})
export class UserApiService {
  private readonly logger = inject(LoggerService).scope('UserApiService');
  
  private apiBaseUrl = `${environment.API_BASE_URL}/protected/users`;

  constructor(
    private http: HttpClient
  ) {
    this.logger.debug('Initialized');
  }

  /**
   * GET /me - Retrieve user profile
   * Returns 404 if user doesn't exist in backend
   */
  public getMe(): Observable<UserResponseSchema> {
    return this.http.get<UserResponseSchema>(`${this.apiBaseUrl}/me`).pipe(
      catchError(error => {
        this.logger.error('Error fetching user profile:', error);
        return throwError(() => new Error('Failed to fetch user profile'));
      })
    );
  }

  /**
   * PUT /me - Create or sync user (idempotent)
   * Creates user if not exists, syncs if exists
   */
  public createOrSyncMe(): Observable<UserResponseSchema> {
    this.logger.debug('Starting createOrSyncMe request');
    return this.http.put<UserResponseSchema>(`${this.apiBaseUrl}/me`, {}).pipe(
      catchError(error => {
        this.logger.error('Error creating/syncing user:', error);
        this.logger.error('Error details:', {
          status: error?.status,
          statusText: error?.statusText,
          message: error?.message,
          error: error?.error,
          url: error?.url
        });
        // Preserve backend error code for upstream handling
        const backendCode = error?.error?.detail?.code;
        const backendMessage = error?.error?.detail?.message;
        const err = new Error(backendMessage || 'Failed to create or sync user profile');
        (err as any).code = backendCode;
        return throwError(() => err);
      })
    );
  }

  /**
   * PATCH /me - Update user fields
   */
  public updateMe(data: UserProfileUpdateInput): Observable<UserResponseSchema> {
    return this.http.patch<UserResponseSchema>(`${this.apiBaseUrl}/me`, data).pipe(
      catchError(error => {
        this.logger.error('Error updating user profile:', error);
        return throwError(() => new Error('Failed to update user profile'));
      })
    );
  }

  /**
   * DELETE /me/personal-data - Delete personal data
   */
  public deletePersonalData(): Observable<void> {
    return this.http.delete<void>(`${this.apiBaseUrl}/me/personal-data`).pipe(
      catchError(error => {
        this.logger.error('Error deleting personal data:', error);
        return throwError(() => new Error('Failed to delete personal data'));
      })
    );
  }

  /**
   * DELETE /me - Delete user account
   */
  public deleteMe(): Observable<void> {
    return this.http.delete<void>(`${this.apiBaseUrl}/me`).pipe(
      catchError(error => {
        this.logger.error('Error deleting user account:', error);
        return throwError(() => new Error('Failed to delete user account'));
      })
    );
  }

  /**
   * GET /me/auth-providers - Get user's authentication providers
   */
  public getAuthProviders(): Observable<AuthProvidersResponse> {
    return this.http.get<AuthProvidersResponse>(`${this.apiBaseUrl}/me/auth-providers`).pipe(
      catchError(error => {
        this.logger.error('Error fetching auth providers:', error);
        return throwError(() => new Error('Failed to fetch auth providers'));
      })
    );
  }
}
